import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { activeLimitsSelector, useUserLimits } from '@/feature/responsible-gaming/hooks/use-user-limits';
import { useActiveWallet } from '@/hooks/use-active-wallet';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { PaymentMethodTypes } from '../const';
import { getDepositErrorMessage } from '../utils';
import { PaymentMethodType } from './use-deposit-payment-methods';

type UseDepositValidationProps = {
    amount: number;
    selectedPaymentMethodType?: PaymentMethodType;
};

export const useDepositAmountValidation = ({ amount, selectedPaymentMethodType }: UseDepositValidationProps) => {
    const [errorMessage, setErrorMessage] = useState('');
    const { t } = useTranslation('wallet');
    const { wallets } = useActiveWallet();
    const settings = useJurisdictionStore(state => state.jurisdictionSettings?.globalSettings?.keyValuePairs);
    const { data: depositLimitsData, isLoading: isDepositLimitsLoading } = useUserLimits({
        select: activeLimitsSelector('DEPOSIT'),
    });
    const depositLimit = settings?.TransactionSettings?.single_deposit_limit || '50000';
    const minimumDeposit: string = settings?.TransactionSettings?.minimum_deposit || '10';
    const socialWalletBalance = parseFloat(wallets[Product.Sweeps].CASH?.real_amount || '0');

    useEffect(() => {
        if (amount === 0) {
            setErrorMessage('');
            return;
        }

        const depositAmountError = getDepositErrorMessage(
            parseFloat(depositLimit),
            amount.toString(),
            depositLimitsData,
            minimumDeposit
        );

        if (depositAmountError) {
            setErrorMessage(depositAmountError);
            return;
        }

        const socialWalletInsufficientFundsError =
            selectedPaymentMethodType === PaymentMethodTypes.SocialWallet && amount > socialWalletBalance
                ? t('social_wallet_insufficient_funds', {
                      balance: toLocaleCurrency(socialWalletBalance),
                  })
                : '';

        setErrorMessage(socialWalletInsufficientFundsError);
    }, [amount, depositLimit, depositLimitsData, minimumDeposit, selectedPaymentMethodType, socialWalletBalance, t]);

    return { errorMessage, isDepositLimitsLoading };
};
